/**
 * Site copy supplied in the brief. Wording is kept as supplied (§59); nothing
 * here may be strengthened without Kabex's approval.
 *
 * Deliberately NOT here yet, pending Kabex:
 *   - "Why import with Kabex" claims (§12)     — Kabex supplied details 2026-10-21
 *   - BE FORWARD agent status (§6)            — confirmation, A28
 *   - Client organisations (§13)              — display permission, A24
 *
 * The payment figures are the one exception to "as supplied": they are no
 * longer written out here. Kabex revised them on 2026-10-21 — an import opens
 * at 30% or 50% of CIF, the balance may be paid in instalments, and the
 * deadline is no longer tied to Mombasa — so every term is interpolated from
 * `content/paymentTerms.ts`. Change the deal there, not here.
 */

import { type ContentItem, type IconKey } from './contentTypes';
import { BALANCE_DEADLINE, DEPOSIT_RANGE, deposit, installments, ownership } from './paymentTerms';

/* Re-exported, not declared: `content/paymentTerms.ts` needs both and this
   module imports it, so declaring them here made the two files a cycle. Every
   existing `from '@/content/site'` import of these types still resolves.
   Imported as well as re-exported because this module uses them itself — a
   bare `export … from` binds nothing in local scope. */
export { type ContentItem, type IconKey };

export const hero = {
  headline: "Don't dream it. Drive it.",
  supporting: 'Source a car from around the world, with free guidance on the model, year and import costs before you commit.',
  trustPoints: [
    { icon: 'verified', title: 'Verified Cars' },
    { icon: 'price', title: 'Competitive Prices' },
    { icon: 'shield', title: 'Trusted Importation' },
  ] satisfies ContentItem[],
};

/**
 * §7 — the services, as named.
 *
 * `as const` rather than a plain `ContentItem[]`, so the titles are a literal
 * union and not `string`. That union is what `content/services.ts` keys its
 * catalogue by, which makes "every supplied service has a page" something the
 * compiler checks rather than something a reader has to count: add a service
 * here and the catalogue stops compiling until it has copy. `satisfies` keeps
 * the shape honest in the same breath, so an icon outside `IconKey` is still
 * an error.
 *
 * ## Renamed 2026-10-21
 *
 * Four names changed in the service restructure and the list went from eight
 * to seven. It now has six entries after Kabex confirmed there is no owned
 * vehicle warehouse to offer. The
 * renames say what the work is in the words a buyer searches with — "car
 * importation" rather than "car sales" for a company that runs no showroom,
 * "local vehicle registration" for a registration that happens in Uganda,
 * "clearing, forwarding and logistics" for optional work after the port,
 * including transit or inland transport when the package calls for it.
 *
 * The merge is the substantive one. "Tax finance" and "payment support" were
 * two pages about money that between them covered one continuous problem —
 * what an import costs you and when each part of it falls due — split at the
 * arbitrary point where the money stops going to Kabex and starts going to
 * URA. A buyer does not experience that as two services, and the two pages
 * each answered half of the question the other raised. They are now one
 * service, `tax-financing`, and the old URLs redirect to it (public/.htaccess).
 *
 * Handover and any clearing or inland transport are described against the
 * package in the customer's order.
 */
export const services = [
  { icon: 'car', title: 'Car importation' },
  { icon: 'finance', title: 'Tax financing' },
  { icon: 'registration', title: 'Local vehicle registration' },
  { icon: 'consult', title: 'Import consultation' },
  { icon: 'clearing', title: 'Clearing, forwarding and logistics' },
  { icon: 'quote', title: 'Free tax inquiries and quotations' },
] as const satisfies readonly ContentItem[];

/**
 * One entry of the supplied list: a §7 name and its icon, and nothing else.
 *
 * Narrower than `ContentItem` on purpose — anything drawing the supplied list
 * is drawing exactly these, so a component typed on this cannot be
 * handed an arbitrary icon-and-title pair that has no service page behind it.
 */
export type SuppliedService = (typeof services)[number];

/** One of the supplied service names. See `content/services.ts`. */
export type ServiceTitle = SuppliedService['title'];

/** The shared introduction used wherever the site explains why Kabex. */
export const whyKabexIntroduction =
  'Get free guidance before you choose, a formal quotation before you commit, and clear progress updates as your import moves forward.';

/** §11 — the shared reasons to choose Kabex, using the details Kabex confirmed on 2026-10-21. */
export const whyChooseKabex: ContentItem[] = [
  {
    icon: 'payment',
    title: `${DEPOSIT_RANGE} of the CIF price starts the process`,
    text: `${deposit.body} The balance is due ${BALANCE_DEADLINE}, and it can be paid in instalments rather than in one transfer.`,
  },
  {
    icon: 'location',
    title: 'Flexible sourcing and handover',
    text: 'Kabex can source vehicles from markets around the world. Your quotation records the agreed destination and the services in your package.',
  },
  {
    icon: 'route',
    title: 'Established transport relationships',
    text: 'As a continuing client, Kabex works with reliable carriage partners to secure favourable transport offers and coordinate inland delivery to your agreed destination.',
  },
  {
    icon: 'tax',
    title: 'Free advice before you choose',
    text: 'Get free guidance on the model, year and current URA rules before you decide. Passenger vehicles aged 9–15 years are listed with a 50% environmental levy; we check the current treatment for your vehicle.',
  },
  {
    icon: 'fraud',
    title: 'A vehicle checked before shipment',
    text: 'Each vehicle is inspected by JEVIC before shipping. Our team can also walk you through the available auction sheet and vehicle details.',
  },
  {
    icon: 'updates',
    title: 'Clear communication at every stage',
    text: 'Start with a free consultation, receive a formal quotation, and get progress updates as your import moves through the agreed process.',
  },
  {
    icon: 'newCars',
    title: 'Choice shaped around your needs',
    text: 'Tell us your budget, preferred model and how you will use the vehicle. We can look across global markets and advise on suitable options.',
  },
  /* Added 2026-10-21. Not a new claim — the vehicle-registration service has
     said it since launch — but Kabex named it as one of the three things to
     lead with, and a §11 tile is where a visitor comparing us to a bond will
     actually meet it. It is deliberately LAST in the array and therefore last
     in the bento: `footerPromo` below picks items by index, and anything
     inserted mid-array would silently change the footer. */
  {
    icon: 'registration',
    title: ownership.headline,
    text: ownership.body,
  },
  /* Added 2026-10-21 alongside it. The instalment claim needs to appear in the
     band that answers "why Kabex" because it is a differentiator, not merely a
     payment mechanic — but the figures stay off the public site (see
     `content/paymentTerms.ts`), so this tile states the mechanics only. */
  {
    icon: 'finance',
    title: installments.headline,
    text: installments.body,
  },
];

/** §9 — what the sales team does for you. */
export const salesTeamHelp: ContentItem[] = [
  { icon: 'search', title: 'Identifying suitable cars' },
  { icon: 'handshake', title: 'Assisting you during negotiations' },
  { icon: 'quote', title: 'Providing quotations' },
  {
    icon: 'calendar',
    title: 'Advising on vehicle years allowed for importation into Uganda under applicable laws',
  },
  { icon: 'updates', title: 'Updating you throughout the importation process' },
  { icon: 'chat', title: 'Responding quickly to enquiries' },
];

export type ImportStep = {
  number: string;
  title: string;
  text: string;
  /**
   * A second sentence, for the bands that have room for one.
   *
   * The revised payment terms (2026-10-21) are two facts per step, not one:
   * step 03 is a pair of shares AND which one applies, step 05 is a deadline
   * AND the option to pay it in instalments. Both in `text` made the sentence
   * roughly twice as long, and `/import-a-car` draws these six steps in a
   * SIX-column track where a column is about 180px — the old one-fact
   * sentence set in five lines there and the two-fact one sets in eight,
   * which that band's own comment calls a stack of slivers.
   *
   * So the extra fact is a separate field rather than a longer `text`, and
   * rather than a second terser copy of the step (which would be two strings
   * to keep in agreement, and this array is read by four bands). Dense
   * layouts render `text`; roomy ones render both. Nothing is duplicated and
   * nothing is dropped — the detail is always reachable on the pages that
   * exist to give it.
   */
  detail?: string;
};

/**
 * §10 — the import journey. Payment terms confirmed by Kabex on 2026-09-17
 * (A12) and REVISED by Kabex on 2026-10-21: step 03 is a pair of shares rather
 * than a flat half, step 05 may be paid in instalments, and neither names a
 * port. All three figures come from `content/paymentTerms.ts`.
 *
 * The handover point and the services after the arrival port depend on the
 * customer's package, so this journey does not promise one standard route.
 */
export const importSteps: ImportStep[] = [
  { number: '01', title: 'Choose', text: 'Identify the vehicle you want.' },
  { number: '02', title: 'Quote', text: 'Receive pricing and guidance.' },
  {
    number: '03',
    title: 'Start',
    text: `Pay ${DEPOSIT_RANGE} of the CIF price to start the process.`,
    detail: 'Which of the two applies depends on the car; your quotation says which.',
  },
  { number: '04', title: 'Import', text: 'Kabex keeps you updated throughout the importation.' },
  {
    number: '05',
    title: 'Balance',
    text: `Clear the balance ${BALANCE_DEADLINE}.`,
    detail: 'It can be paid in instalments across the voyage rather than in one transfer.',
  },
  { number: '06', title: 'Clear & hand over', text: 'The agreed destination and services are recorded in your order.' },
];

/**
 * The glyph each §10 step is drawn with, keyed by its number.
 *
 * It lives here, beside the steps, because all three bands that tell the
 * sequence now draw marks: the home page's phase spine, the `/how-it-works`
 * timeline and the `/import-a-car` track. It used to be declared three times
 * — byte-identical in `useJourneySteps`, `useImportJourneySteps` and nowhere
 * on the home page, which was the one band without icons. Three copies of one
 * mapping is three chances for the sequence to drift, so there is one.
 *
 * The icons are decorative: each one restates the title set directly beside
 * it, so nothing here is a claim and none of it needs Kabex's sign-off the way
 * the wording above does.
 */
export const importStepIcons: Record<string, IconKey> = {
  '01': 'search',
  '02': 'quote',
  '03': 'payment',
  '04': 'route',
  '05': 'calendar',
  '06': 'clearing',
};

/** §6 — the five values. */
export const values: ContentItem[] = [
  { icon: 'verified', title: 'Transparency' },
  { icon: 'shield', title: 'Reliability' },
  { icon: 'handshake', title: 'Honesty' },
  { icon: 'customer', title: 'Customer Priority' },
  { icon: 'team', title: 'Teamwork' },
];

/** One slide per supplied car photo; the car itself lives in config/heroVehicles.ts. */
export type HeroSlideId = 'toyota' | 'audi' | 'mercedes-benz' | 'bmw' | 'ford';

export type HeroSlide = {
  id: HeroSlideId;
  /** The make shown on the slide: names its dot in the carousel controls. */
  label: string;
  /** Small line above the headline. */
  eyebrow: string;
  headline: string;
  supporting: string;
};

/**
 * Hero slides (§4), one per car in the slideshow. The CTAs do not change
 * between slides — IA §4 fixes them as Find My Car, Import a Car and WhatsApp
 * Us — so each slide varies only the words above them.
 *
 * The slides use the current services and differentiators: global sourcing,
 * agreed destinations, cost guidance, and progress updates.
 */
export const heroSlides: HeroSlide[] = [
  {
    id: 'toyota',
    label: 'Toyota',
    eyebrow: 'Japan · China · UK · Worldwide',
    headline: hero.headline,
    supporting: hero.supporting,
  },
  {
    id: 'audi',
    label: 'Audi',
    eyebrow: 'Clearing, forwarding and logistics',
    headline: 'Your CIF destination, agreed in advance.',
    supporting:
      'Your quotation confirms the CIF destination and whether clearing or onward transport is included.',
  },
  {
    id: 'mercedes-benz',
    label: 'Mercedes-Benz',
    eyebrow: 'Plan your budget',
    headline: 'Know the cost before you commit.',
    supporting: 'Free tax inquiries and quotations, and calculators that estimate your costs.',
  },
  {
    id: 'bmw',
    label: 'BMW',
    eyebrow: 'Sourced to your spec',
    headline: 'Tell us the car. We find it.',
    supporting: 'Share the make, model, year and budget, and we search markets around the world for the right options.',
  },
  {
    id: 'ford',
    label: 'Ford',
    eyebrow: 'Updates all the way',
    headline: 'Know where your car is.',
    supporting:
      'Updates from purchase to delivery, so you always know the stage your car has reached.',
  },
];

/**
 * Footer brand column (decided 2026-09-24). PLACEHOLDER copy: the pitch and
 * bullets reuse approved wording until Kabex supplies the footer's own.
 */
export const footerPromo = {
  pitch: hero.supporting,
  highlights: [whyChooseKabex[0], whyChooseKabex[3], whyChooseKabex[2]] satisfies ContentItem[],
  whatsappMessage: 'Hello Kabex Motors, I would like help importing a car.',
};

/** §25: shown beside every estimate, never hidden in a footer. */
export const estimateDisclaimers = [
  'These figures are estimates, not an official quotation.',
  'Prices and taxes can change.',
  'URA taxes depend on several factors, including the vehicle’s value, age and engine size.',
  'Exchange-rate movements can change the final amount.',
  'Your final tax must be confirmed through official URA channels.',
];

/**
 * The copy a WhatsApp-only number carries wherever it is published.
 *
 * Here rather than in the component because it is read in three places — the
 * footer card, the mobile drawer and /contact — and three copies of a sentence
 * is three chances for them to disagree about what the Japan desk does.
 *
 * `badge` is the visible label, which is the part that works on a phone with
 * no cursor; the fuller explanation is the number's own `note` in
 * `config/company.ts`, carried by the hint beside it.
 *
 * `message` is addressed to the desk it opens, not to the page it was pressed
 * on. Every other WhatsApp control on the site inherits the route's message
 * (`usePageWhatsAppMessage`), which would send a tax-calculator enquiry to a
 * sourcing desk that does not answer tax questions.
 */
export const whatsappOnlyPhone = {
  badge: 'WhatsApp only',
  message: 'Hello Kabex Motors, I would like to ask your Japan sourcing desk about a vehicle.',
};
